import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaMyAccountPagesNavEvent } from "@stoneage/App/Utilities/DataLayer";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentOrdersStyles {
    orderSummaryCard?: OrderSummaryCardStyles;
    widgetContainer?: InjectableCss;
    titleWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataCell?: InjectableCss;
}

export const recentOrdersStyles: RecentOrdersStyles = {
    titleWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            margin-bottom: 12px;
        `,
    },
    tableWrapper: {
        css: css`
            width: 100%;
            overflow: auto;
        `,
    },
    contentWrapper: {
        css: css``,
    },
};

const defaultCustomCss = `
.view-all-link{
}

.card-list-heading{
}

.card-list-body{
}

.card_container__wrapper{
}

.order-date-grid-item{

} 
.order-number-grid-item{
    
} 
.order-po-grid-item{
    
} 
.order-status-grid-item{
    
} 
.order-total-grid-item{
    
} `;

let recentOrdersParameter: GetOrdersApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "OrderDate DESC",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        const { title } = this.props.fields;

        const orders = ordersDataView?.value;

        if (!orders?.length) {
            return null;
        }

        return (
            <StyledWrapper {...customCssWrapper} {...styles.widgetContainer}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography
                        as="h3"
                        variant="h3"
                        css={css`
                            margin-bottom: 0;
                            text-transform: capitalize;
                        `}
                    >
                        {title}
                    </Typography>
                    <Link
                        href={orderHistoryUrl}
                        color="secondary"
                        className="view-all-link"
                        css={css`
                            > span {
                                display: flex;
                                align-items: center;
                                margin-left: 32px;
                                font-size: 15px;
                                font-weight: bold;
                            }
                        `}
                        onClick={() => {
                            // XNG-Change: track GA4
                            orderHistoryUrl && trackGaMyAccountPagesNavEvent(
                                "myacct_orderhistory",
                                orderHistoryUrl,
                                translate("View All"),
                                this.props.impersonation,
                                this.props.customerNumber
                            );
                        }}
                    >
                        {translate("View All")}
                        <Icon
                            src="ChevronRight"
                            size={14}
                            color="secondary"
                            css={css`
                                margin-left: 5px;
                            `}
                        ></Icon>
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.contentWrapper} className="card_container__wrapper">
                    {orders && orders.length !== 0 && (
                        <StyledWrapper {...styles.tableWrapper}>
                            <DataTable {...styles.dataTable} className="mobile-view">
                                <DataTableHead {...styles.dataTableHead} className="card-list-heading">
                                    <DataTableHeader title={translate("Order Date")} {...styles.dataTableHeaders}>
                                        {translate("Order Date")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Order Number")} {...styles.dataTableHeaders}>
                                        {translate("Order Number")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("PO Number")} {...styles.dataTableHeaders}>
                                        {translate("PO Number")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Status")} {...styles.dataTableHeaders}>
                                        {translate("Status")}
                                    </DataTableHeader>
                                    <DataTableHeader title={translate("Amount")} {...styles.dataTableHeaders}>
                                        {translate("Total")}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBody} className="card-list-body">
                                    {(ordersDataView.value || []).map(
                                        order =>
                                            order !== null && (
                                                <DataTableRow
                                                    {...styles.dataTableRow}
                                                    data-test-selector="recentOrders_dataLine"
                                                    key={order.id}
                                                >
                                                    <OrderSummaryCard
                                                        order={order}
                                                        orderSettings={settingsCollection.orderSettings}
                                                        extendedStyles={styles.orderSummaryCard}
                                                    />
                                                </DataTableRow>
                                            ),
                                    )}
                                </DataTableBody>
                            </DataTable>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
